// Client-side mirror of backend/app/websites/{hostnames,domain_verification}.py.
// Used only for display before the server has answered (the create wizard and
// the "type a domain" preview). Whenever the API returns `records`, show those.

// Differs per environment (QA: sites.pearlcrm.com.au, production: sites.pearlcrm.com);
// must match the backend's WEBSITE_PLATFORM_DOMAIN_SUFFIX.
export const PLATFORM_DOMAIN_SUFFIX = (process.env.REACT_APP_WEBSITE_PLATFORM_DOMAIN_SUFFIX || "sites.pearlcrm.com.au")
  .trim()
  .replace(/^\.+|\.+$/g, "")
  .toLowerCase();
export const VERIFY_TXT_PREFIX = "_pearlcrm-verify";
export const VERIFY_TXT_VALUE_PREFIX = "pearlcrm-site-verification=";
export const SLUG_MAX_LENGTH = 40;
export const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])?$/;

// Two-part public suffixes that make a three-label name an apex domain.
export const TWO_PART_SUFFIXES = ["com.au", "net.au", "org.au", "co.uk", "co.nz", "com.sg", "co.in"];

const HOSTNAME_PATTERN = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z][a-z0-9-]{0,62}[a-z0-9]$/;

export function slugify(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, SLUG_MAX_LENGTH)
    .replace(/-+$/g, "");
}

export function isValidSlug(slug) {
  return SLUG_PATTERN.test(String(slug || ""));
}

export function platformHostname(slug) {
  return slug ? `${slug}.${PLATFORM_DOMAIN_SUFFIX}` : "";
}

export function normalizeDomain(value) {
  let domain = String(value || "").trim().toLowerCase();
  domain = domain.replace(/^[a-z][a-z0-9+.-]*:\/\//, "");
  domain = domain.split(/[/?#]/)[0];
  domain = domain.replace(/:\d+$/, "").replace(/\.+$/, "");
  return domain;
}

export function isValidDomain(value) {
  const domain = normalizeDomain(value);
  if (!HOSTNAME_PATTERN.test(domain)) return false;
  return !(domain === PLATFORM_DOMAIN_SUFFIX || domain.endsWith(`.${PLATFORM_DOMAIN_SUFFIX}`));
}

function registrableLabelCount(labels) {
  const lastTwo = labels.slice(-2).join(".");
  return labels.length >= 3 && TWO_PART_SUFFIXES.includes(lastTwo) ? 3 : 2;
}

// "apex" | "subdomain" | null (for an empty or malformed name).
export function classifyDomain(value) {
  const domain = normalizeDomain(value);
  if (!HOSTNAME_PATTERN.test(domain)) return null;
  const labels = domain.split(".");
  return labels.length <= registrableLabelCount(labels) ? "apex" : "subdomain";
}

// The zone a customer edits in their DNS provider, e.g. "abc.com.au".
export function registrableDomain(value) {
  const domain = normalizeDomain(value);
  const labels = domain.split(".");
  return labels.slice(-registrableLabelCount(labels)).join(".");
}

// Records the customer must add. Unknown server-side values (apex IPs, the
// verification token) come back with `value: null` and `pending: true`.
export function expectedRecords(value, { platformHost = "", apexIps = [], token = "" } = {}) {
  const domain = normalizeDomain(value);
  const kind = classifyDomain(domain);
  if (!kind) return [];
  const records = [];
  if (kind === "subdomain") {
    const zone = registrableDomain(domain);
    const label = domain.slice(0, domain.length - zone.length - 1);
    records.push({
      type: "CNAME",
      name: label,
      value: platformHost || null,
      pending: !platformHost,
      purpose: "Routes visitors to your PearlCRM website.",
    });
  } else if (apexIps.length) {
    apexIps.forEach((ip) => {
      records.push({
        type: "A",
        name: "@",
        value: ip,
        purpose: "Routes visitors to your PearlCRM website. An ALIAS, ANAME or flattened CNAME to the PearlCRM address also works.",
      });
    });
  } else {
    records.push({
      type: "A",
      name: "@",
      value: null,
      pending: true,
      purpose: "Routes visitors to your PearlCRM website. An ALIAS, ANAME or flattened CNAME to the PearlCRM address also works.",
    });
  }
  if (kind === "apex") {
    records.push({
      type: "CNAME",
      name: "www",
      value: platformHost || null,
      pending: !platformHost,
      purpose: `Sends www.${domain} to your website, which redirects visitors to ${domain}.`,
    });
  }
  records.push({
    type: "TXT",
    name: `${VERIFY_TXT_PREFIX}.${domain}`,
    value: token ? `${VERIFY_TXT_VALUE_PREFIX}${token}` : null,
    pending: !token,
    purpose: "Proves you own this domain.",
  });
  return records;
}
